/**
 * Lo que la pantalla de `/usage` deriva del payload, sin React y sin JSX.
 *
 * Tres reglas gobiernan este archivo y explican por qué casi todo devuelve
 * `null` en vez de un número:
 *
 *  - **`null` nunca es cero.** Una cuenta sin medición no consumió cero: no se
 *    sabe cuánto consumió. Las severidades devuelven `unknown` y la pantalla
 *    dibuja "Sin dato", nunca un semáforo verde por defecto.
 *  - **Ningún porcentaje se calcula acá.** Los porcentajes vienen del contrato
 *    con su base al lado (`Variacion`, `Participacion`, `CadenaActivacion`). Lo
 *    único que este archivo hace con ellos es **repartirlos en segmentos** y
 *    **escribir su base en palabras**.
 *  - **Los umbrales llegan del payload** (`umbralCaidaPct`,
 *    `umbralAgotamientoMeses`), no se hardcodean: son decisiones de producto que
 *    el servidor publica justamente para que la pantalla pueda decirlas en vez
 *    de dejarlas implícitas en el título de un bloque.
 */
import type {
  Agent,
  CadenaActivacion,
  CreditBalance,
  DeltaBase,
  Health,
  ProyeccionAgotamiento,
  QuemaPromedio,
  Unit,
  UsageClientRow,
  Variacion,
} from '@ops/contracts'
import { formatMetricValue, formatPeriodoCorto } from '@ops/contracts'

import type { ShareSegment } from '../../ui'
import { segmentosDeUnEje } from '../comunes'

// ---------------------------------------------------------------------------
// Severidades — el color de una fila, siempre acompañado de su texto
// ---------------------------------------------------------------------------

/**
 * Severidad de una caída de consumo.
 *
 * El umbral es el del payload (−30%): a partir de ahí la cuenta entra en el
 * bloque de caída y va en crítico. Entre 0 y el umbral es ámbar — todavía no
 * dispara la alerta pero viene para ese lado —, y crecer o quedarse igual es
 * verde. Sin variación medible no hay color: es `unknown`.
 */
export function severidadDeCaida(
  pct: number | null | undefined,
  umbralPct: number,
): Health {
  if (pct === null || pct === undefined) return 'unknown'
  if (pct <= umbralPct) return 'critical'
  if (pct < 0) return 'warning'
  return 'ok'
}

/**
 * Severidad de la autonomía de créditos, en meses.
 *
 * Por debajo de la mitad del umbral la conversación comercial ya está tarde
 * (crítico); entre la mitad y el umbral es ámbar; arriba, verde. Sin proyección
 * —porque no hay saldo o porque la cuenta no quema— no hay color.
 */
export function severidadDeAgotamiento(
  meses: number | null | undefined,
  umbralMeses: number,
): Health {
  if (meses === null || meses === undefined) return 'unknown'
  if (meses < umbralMeses / 2) return 'critical'
  if (meses < umbralMeses) return 'warning'
  return 'ok'
}

/** Días de un trimestre. Un agente instalado que no produce hace un trimestre
 *  ya no es un arranque lento: es plata contratada que no corre. */
export const DIAS_CRITICO_SIN_USO = 90
export const DIAS_ATENCION_SIN_USO = 30

export function severidadSinUso(dias: number | null | undefined): Health {
  if (dias === null || dias === undefined) return 'unknown'
  if (dias >= DIAS_CRITICO_SIN_USO) return 'critical'
  if (dias >= DIAS_ATENCION_SIN_USO) return 'warning'
  return 'ok'
}

/**
 * Un agente sin medición **no tiene severidad de uso**: no se sabe si produce.
 * Va siempre en `unknown`, que es el estado honesto, y la fila lo dice con
 * palabras además del gris.
 */
export function severidadDeAgente(agente: Agent): Health {
  if (agente.consumo === null) return 'unknown'
  if (agente.consumo.trazas > 0) return 'ok'
  return severidadSinUso(agente.diasSinUso)
}

/**
 * Severidad del margen de una cuenta. **Solo se usa en el bloque de admin.**
 *
 * Los cortes son los que el panel ya usa para margen bruto de software: por
 * debajo de 50% la cuenta no paga su costo de servir con holgura, y por debajo de
 * 70% queda lejos de lo que el resto del portfolio sostiene. Sin porcentaje
 * —porque el precio es cero, o falta uno de los dos términos— no hay color: es
 * `unknown`, y la fila lo dice con palabras.
 */
export const MARGEN_CRITICO_PCT = 50
export const MARGEN_ATENCION_PCT = 70

export function severidadDeMargen(pct: number | null | undefined): Health {
  if (pct === null || pct === undefined) return 'unknown'
  if (pct < MARGEN_CRITICO_PCT) return 'critical'
  if (pct < MARGEN_ATENCION_PCT) return 'warning'
  return 'ok'
}

// ---------------------------------------------------------------------------
// Bases publicadas — la regla 5 escrita en palabras
// ---------------------------------------------------------------------------

/**
 * Un costo **unitario** en dólares, con los decimales que hacen falta para que
 * la cuenta cierre.
 *
 * `formatMetricValue(x, 'usd')` redondea a dos decimales abajo de mil, que es lo
 * correcto para un ticket o un MRR y **falso para un costo por traza**: 0,023803
 * se imprime `$0,02`, y ahí la fila héroe queda afirmando dos cosas que no cierran
 * entre sí — 157.617 trazas × $0,02 da $3.152, no el $3.752 que la tarjeta
 * publica arriba. Una base que no reproduce su total es peor que no publicarla:
 * invita a hacer la cuenta y devuelve otro número.
 *
 * Así que abajo de un dólar se muestran cuatro decimales. No se toca `formatUsd`
 * del contrato, que para el resto del panel está bien: el problema no es el
 * formateador, es usar el formato de precio para una tarifa unitaria.
 */
export function formatUsdUnitario(valor: number): string {
  if (Math.abs(valor) >= 1 || valor === 0) return formatMetricValue(valor, 'usd')
  const cuerpo = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(Math.abs(valor))
  return `${valor < 0 ? '−' : ''}$${cuerpo}`
}

/**
 * Un término de la base de un KPI, en su unidad. Los USD chicos pasan por
 * `formatUsdUnitario` para que la base sea recalculable, ver arriba.
 */
export function formatValorDeBase(
  valor: number | null,
  unidad: Unit,
): string | null {
  if (valor === null) return null
  return unidad === 'usd'
    ? formatUsdUnitario(valor)
    : formatMetricValue(valor, unidad)
}

/** La base de una variación, en la forma que espera `DeltaTag`. */
export function baseDeVariacion(
  variacion: Variacion | null | undefined,
): DeltaBase | null {
  if (variacion === null || variacion === undefined) return null
  return { periodo: variacion.base.periodo, valor: variacion.base.valor }
}

/**
 * Fecha 'YYYY-MM-DD' → 'dd/mm/aaaa', sin pasar por `Date`.
 *
 * `new Date('2026-08-18')` es medianoche UTC y en Buenos Aires se imprimiría el
 * 17: una fecha de agotamiento corrida un día es exactamente el tipo de error
 * que nadie audita. Y con año, a diferencia de `formatFechaCorta`: una
 * proyección mira al futuro y "18/08" a secas no dice de qué año.
 */
export function formatFechaDia(iso: string | null): string | null {
  if (iso === null) return null
  const [anio, mes, dia] = iso.slice(0, 10).split('-')
  if (anio === undefined || mes === undefined || dia === undefined) return iso
  return `${dia}/${mes}/${anio}`
}

/**
 * La aritmética de la proyección, dicha entera: saldo ÷ quema = meses.
 *
 * Es la regla de números derivables aplicada al bloque que más la necesita: una
 * fecha de agotamiento sin sus dos insumos a la vista es una predicción que
 * nadie puede discutir.
 */
export function explicacionProyeccion(p: ProyeccionAgotamiento): string {
  return `${formatMetricValue(p.base.saldo, 'count')} de saldo ÷ ${formatMetricValue(
    p.base.quemaMensual,
    'count',
  )} de quema mensual = ${formatMetricValue(p.meses, 'months')}`
}

/** Los meses exactos que promedió la quema, con su consumo. La base de la base. */
export function explicacionQuema(q: QuemaPromedio): string {
  const meses = q.base
    .map(
      (punto) =>
        `${formatPeriodoCorto(punto.periodo)} ${formatMetricValue(punto.creditos, 'count')}`,
    )
    .join(' · ')
  return `Promedio de ${q.meses} ${q.meses === 1 ? 'mes medido' : 'meses medidos'}: ${meses}`
}

// ---------------------------------------------------------------------------
// La cadena contratado → instalado → activo
// ---------------------------------------------------------------------------

/**
 * La cadena repartida en tres segmentos que **suman los contratados**: activos,
 * instalados sin uso y sin instalar.
 *
 * Es la aportación central del módulo hecha barra: el tramo ámbar del medio es
 * lo que la brecha de activación de Clientes contaba como éxito. Los tres
 * porcentajes se calculan sobre el mismo total y por eso se pueden leer juntos;
 * `ShareBar` imprime cantidad y porcentaje en la leyenda, así que la base de
 * cada uno queda publicada al lado.
 *
 * El porcentaje **no se escribe acá: lo deriva `segmentosDeUnEje`** de la misma
 * cantidad que la leyenda imprime. Es el invariante de un solo eje que los otros
 * tres call sites de `ShareBar` ya cumplen: un segmento cuyo ancho no cierre con
 * su número deja de ser representable.
 *
 * Devuelve `null` sin contratados: una barra de cero segmentos afirmaría que la
 * empresa no vendió ningún empleado digital.
 */
export function segmentosDeCadena(
  cadena: CadenaActivacion | null,
): ShareSegment[] | null {
  if (cadena === null || cadena.contratados <= 0) return null

  return segmentosDeUnEje(
    [
      {
        id: 'activos',
        label: 'Activos',
        cantidad: cadena.activos,
        severidad: 'ok',
      },
      {
        id: 'instalados-sin-uso',
        label: 'Instalados sin uso',
        cantidad: cadena.brechaUso,
        severidad: 'warning',
      },
      {
        id: 'sin-instalar',
        label: 'Sin instalar',
        cantidad: cadena.brechaInstalacion,
        severidad: 'critical',
      },
    ],
    cadena.contratados,
  )
}

// ---------------------------------------------------------------------------
// Triage de la pantalla
// ---------------------------------------------------------------------------

export interface ConteosUsage {
  agentesSinUso: number
  agentesSinMedicion: number
  cuentasEnCaida: number
  cuentasPorAgotar: number
  cuentasSanas: number
  /** Cuentas del período que no tienen ninguna medición de consumo. */
  cuentasSinMedicion: number
}

export function conteosUsage(input: {
  agentesSinUso: readonly Agent[]
  agentesSinMedicion: readonly Agent[]
  cuentasEnCaida: readonly UsageClientRow[]
  cuentasPorAgotarCreditos: readonly CreditBalance[]
  porCliente: readonly UsageClientRow[]
  cuentasSanas: { cantidad: number }
}): ConteosUsage {
  return {
    agentesSinUso: input.agentesSinUso.length,
    agentesSinMedicion: input.agentesSinMedicion.length,
    cuentasEnCaida: input.cuentasEnCaida.length,
    cuentasPorAgotar: input.cuentasPorAgotarCreditos.length,
    cuentasSanas: input.cuentasSanas.cantidad,
    cuentasSinMedicion: input.porCliente.filter(
      (fila) => fila.creditosConsumidos === null,
    ).length,
  }
}

/**
 * El veredicto de la pantalla, en una oración y por orden de urgencia.
 *
 * No promedia ni puntúa: dice el peor hecho primero, que es como lee una
 * persona que abre el panel para saber qué hacer hoy.
 */
export function titularUsage(conteos: ConteosUsage): {
  estado: Health
  titular: string
} {
  if (conteos.cuentasEnCaida > 0) {
    return {
      estado: 'critical',
      titular:
        conteos.cuentasEnCaida === 1
          ? '1 cuenta se desplomó este mes'
          : `${conteos.cuentasEnCaida} cuentas se desplomaron este mes`,
    }
  }
  if (conteos.agentesSinUso > 0) {
    return {
      estado: 'critical',
      titular:
        conteos.agentesSinUso === 1
          ? '1 empleado digital instalado no produce'
          : `${conteos.agentesSinUso} empleados digitales instalados no producen`,
    }
  }
  if (conteos.cuentasPorAgotar > 0) {
    return {
      estado: 'warning',
      titular:
        conteos.cuentasPorAgotar === 1
          ? '1 cuenta se queda sin créditos'
          : `${conteos.cuentasPorAgotar} cuentas se quedan sin créditos`,
    }
  }
  if (conteos.agentesSinMedicion > 0) {
    return {
      estado: 'warning',
      titular:
        conteos.agentesSinMedicion === 1
          ? '1 empleado digital no se puede medir'
          : `${conteos.agentesSinMedicion} empleados digitales no se pueden medir`,
    }
  }
  if (conteos.cuentasSanas > 0) {
    return {
      estado: 'ok',
      titular:
        conteos.cuentasSanas === 1
          ? '1 cuenta consumiendo sin sobresaltos'
          : `${conteos.cuentasSanas} cuentas consumiendo sin sobresaltos`,
    }
  }
  return {
    estado: 'unknown',
    titular: 'Todavía no hay consumo medido en el período',
  }
}
